(function(){
  'use strict';
  
  // Config
  const sizeNameRegex = /size/i;
  const conditionNameRegex = /condition|cond/i;
  const DEBOUNCE_DELAY = 150;
  const RENDER_BATCH_SIZE = 20;
  
  // DOM cache
  const modelSelect = document.getElementById('cg-model');
  const sizeEl = document.getElementById('cg-size');
  const conditionEl = document.getElementById('cg-condition');
  const actionWrap = document.getElementById('cg-action');
  const dataScript = document.getElementById('cg-collection-data');
  
  if (!dataScript || !modelSelect) return;
  
  // Parse products once
  let products = [];
  try {
    products = JSON.parse(dataScript.textContent || '[]');
  } catch (e) {
    console.error('Failed to parse collection JSON', e);
    return;
  }
  
  // Cache DOM queries
  const optionIndexCache = new WeakMap();
  
  function optionIndexByRegex(product, regex) {
    const cacheKey = regex === sizeNameRegex ? 'size' : 'condition';
    if (!optionIndexCache.has(product)) {
      optionIndexCache.set(product, {});
    }
    const cache = optionIndexCache.get(product);
    if (cache[cacheKey] !== undefined) return cache[cacheKey];
    
    for (let i = 0; i < (product.options || []).length; i++) {
      if (regex.test(product.options[i])) {
        cache[cacheKey] = i;
        return i;
      }
    }
    cache[cacheKey] = -1;
    return -1;
  }
  
  function productHasAvailableVariant(product) {
    return product.variants && product.variants.some(v => v.available);
  }
  
  // Sorting (unchanged)
  function extractFirstNumber(title) {
    const m = title.match(/(\d{1,4})/);
    return m ? parseInt(m[1], 10) : null;
  }
  
  const specialRank = { 'se': 1, 'mini': 2, 'pro': 3, 'pro max': 4, 'max': 5 };
  
  function getSpecialRank(title) {
    const lower = title.toLowerCase();
    for (const key in specialRank) if (lower.includes(key)) return specialRank[key];
    return null;
  }
  
  function modelComparator(a, b) {
    const na = extractFirstNumber(a);
    const nb = extractFirstNumber(b);
    if (na !== null && nb !== null) return nb - na;
    if (na !== null && nb === null) return -1;
    if (na === null && nb !== null) return 1;
    const ra = getSpecialRank(a) ?? 9999;
    const rb = getSpecialRank(b) ?? 9999;
    if (ra !== rb) return ra - rb;
    return a.toLowerCase().localeCompare(b.toLowerCase());
  }
  
  // Cache product lookups - MUST be declared before use
  const productCache = new Map();
  
  function findProductByTitle(title) {
    if (!title) return null;
    if (productCache.has(title)) return productCache.get(title);
    
    const exact = products.find(p => p.title === title);
    if (exact) {
      productCache.set(title, exact);
      return exact;
    }
    
    const lower = title.toLowerCase();
    const found = products.find(p => p.title.toLowerCase() === lower) || null;
    productCache.set(title, found);
    return found;
  }
  
  // Build model lists
  const titleSet = new Set();
  products.forEach(p => titleSet.add(p.title));
  const allTitles = Array.from(titleSet);
  
  const availableModels = [];
  const outOfStockModels = [];
  
  allTitles.forEach(title => {
    const product = findProductByTitle(title);
    if (product && productHasAvailableVariant(product)) {
      availableModels.push(title);
    } else {
      outOfStockModels.push(title);
    }
  });
  
  availableModels.sort(modelComparator);
  outOfStockModels.sort(modelComparator);
  const titles = [...availableModels, ...outOfStockModels];
  
  // Create UI - optimized DOM manipulation
  const wrapper = document.createElement('div');
  wrapper.className = 'cg-model-dropdown';
  wrapper.setAttribute('role', 'combobox');
  wrapper.setAttribute('aria-haspopup', 'listbox');
  wrapper.style.cssText = 'position:relative;width:100%';
  
  const input = document.createElement('input');
  input.type = 'text';
  input.id = 'cg-model-search';
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', 'cg-model-listbox');
  input.setAttribute('placeholder', 'Available Model...');
  input.setAttribute('autocomplete', 'off');
  input.style.width = '100%';
  
  const listbox = document.createElement('ul');
  listbox.id = 'cg-model-listbox';
  listbox.setAttribute('role', 'listbox');
  listbox.style.cssText = 'position:absolute;top:100%;left:0;right:0;z-index:9999;max-height:320px;overflow:auto;margin:6px 0 0 0;padding:6px 0;list-style:none;background:#fff;border:1px solid rgba(0,0,0,0.12);border-radius:6px;box-shadow:0 6px 18px rgba(0,0,0,0.08);display:none;-webkit-overflow-scrolling:touch';
  
  const noRes = document.createElement('div');
  noRes.textContent = 'No results';
  noRes.style.cssText = 'padding:6px 12px;color:#666;display:none';
  
  wrapper.appendChild(input);
  wrapper.appendChild(listbox);
  wrapper.appendChild(noRes);
  
  modelSelect.parentNode.insertBefore(wrapper, modelSelect);
  modelSelect.style.display = 'none';
  
  // Populate hidden select - use fragment for batch DOM operations
  const fragment = document.createDocumentFragment();
  const defaultOption = document.createElement('option');
  defaultOption.value = '';
  defaultOption.textContent = '— Available Model —';
  fragment.appendChild(defaultOption);
  
  titles.forEach(t => {
    const o = document.createElement('option');
    o.value = t;
    o.textContent = t;
    fragment.appendChild(o);
  });
  modelSelect.appendChild(fragment);
  
  // Render list with virtual scrolling for large lists
  let visibleItems = [];
  let renderTimeout;
  
  function renderList(filter) {
    // Debounce rendering
    clearTimeout(renderTimeout);
    renderTimeout = setTimeout(() => {
      performRender(filter);
    }, 50);
  }
  
  function performRender(filter) {
    const term = filter ? filter.toLowerCase() : '';
    const matches = titles.filter(t => t.toLowerCase().includes(term));
    
    if (matches.length === 0) {
      listbox.style.display = 'none';
      noRes.style.display = 'block';
      input.setAttribute('aria-expanded', 'false');
      visibleItems = [];
      return;
    }
    
    noRes.style.display = 'none';
    
    // Use DocumentFragment for batch DOM operations
    const frag = document.createDocumentFragment();
    visibleItems = [];
    
    // Render in batches using requestIdleCallback if available
    const renderBatch = (startIdx) => {
      const endIdx = Math.min(startIdx + RENDER_BATCH_SIZE, matches.length);
      
      for (let i = startIdx; i < endIdx; i++) {
        const t = matches[i];
        const product = findProductByTitle(t);
        const isAvailable = product && productHasAvailableVariant(product);
        
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.dataset.value = t;
        li.tabIndex = -1;
        li.style.cssText = 'padding:8px 12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
        li.textContent = t;
        
        if (isAvailable) {
          li.style.cursor = 'pointer';
          // Support both mouse and touch events for iOS compatibility
          li.addEventListener('mousedown', onItemClick);
          li.addEventListener('touchend', function(e) {
            // Only trigger if it was a tap, not a scroll
            const touchDuration = Date.now() - touchStartTime;
            if (touchDuration < 300) {
              onItemClick(e);
            }
          }, { passive: false });
          li.addEventListener('mouseenter', createMouseEnterHandler(li), { passive: true });
          visibleItems.push(li);
        } else {
          li.style.cssText += ';cursor:not-allowed;opacity:0.45';
          li.setAttribute('aria-disabled', 'true');
        }
        
        frag.appendChild(li);
      }
      
      if (startIdx === 0) {
        listbox.innerHTML = '';
      }
      listbox.appendChild(frag);
      
      if (endIdx < matches.length) {
        if (window.requestIdleCallback) {
          requestIdleCallback(() => renderBatch(endIdx));
        } else {
          setTimeout(() => renderBatch(endIdx), 0);
        }
      }
    };
    
    renderBatch(0);
    listbox.style.display = 'block';
    input.setAttribute('aria-expanded', 'true');
    setActiveIndex(-1);
  }
  
  // Create handler with closure to avoid repeated lookups
  function createMouseEnterHandler(li) {
    return () => setActiveIndex(visibleItems.indexOf(li));
  }
  
  let activeIndex = -1;
  
  function setActiveIndex(i) {
    if (activeIndex === i) return;
    activeIndex = i;
    
    // Batch style updates
    requestAnimationFrame(() => {
      visibleItems.forEach((el, idx) => {
        if (idx === i) {
          el.style.background = '#f3f3f3';
          el.setAttribute('aria-selected', 'true');
          el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        } else {
          el.style.background = 'transparent';
          el.removeAttribute('aria-selected');
        }
      });
    });
  }
  
  function onItemClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const val = e.currentTarget.dataset.value;
    
    // Clear any pending blur timeout
    clearTimeout(blurTimeout);
    
    chooseModel(val);
    // Immediate close without delay
    listbox.style.display = 'none';
    noRes.style.display = 'none';
    input.setAttribute('aria-expanded', 'false');
    setActiveIndex(-1);
    input.blur(); // Remove focus to prevent reopening
  }
  
  function chooseModel(title) {
    input.value = title;
    modelSelect.value = title;
    onModelChosen(title);
  }
  
  function openList() {
    renderList(input.value || '');
  }
  
  function closeList() {
    listbox.style.display = 'none';
    noRes.style.display = 'none';
    input.setAttribute('aria-expanded', 'false');
    setActiveIndex(-1);
  }
  
  function resetSelect(sel, placeholder) {
    sel.innerHTML = '';
    const o = document.createElement('option');
    o.value = '';
    o.textContent = placeholder || '—';
    sel.appendChild(o);
    sel.disabled = true;
  }
  
  function populateDependentOptions(product, autoSelectFirstAvailable = true) {
    const sizeIdx = optionIndexByRegex(product, sizeNameRegex);
    const condIdx = optionIndexByRegex(product, conditionNameRegex);
    
    const sizeSet = new Set();
    const condSet = new Set();
    
    product.variants.forEach(v => {
      if (sizeIdx >= 0) sizeSet.add(v.options[sizeIdx] || '');
      if (condIdx >= 0) condSet.add(v.options[condIdx] || '');
    });
    
    // Size - batch DOM operations
    resetSelect(sizeEl, '— Available Size —');
    if (sizeIdx >= 0 && sizeSet.size) {
      const sizeArray = Array.from(sizeSet).filter(Boolean);


function sortStorage(a, b) {
  function toGB(value) {
    const match = value.match(/([\d.]+)\s*(TB|GB)/i);

    if (!match) return Number.MAX_SAFE_INTEGER;

    const amount = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    return unit === 'TB' ? amount * 1000 : amount;
  }

  return toGB(a) - toGB(b);
}

sizeArray.sort(sortStorage);
      
      const frag = document.createDocumentFragment();
      sizeArray.forEach(val => {
        const o = document.createElement('option');
        o.value = val;
        o.textContent = val;
        frag.appendChild(o);
      });
      sizeEl.appendChild(frag);
      sizeEl.disabled = false;
    }
    
    // Condition - batch DOM operations
    resetSelect(conditionEl, '— Available Condition —');
    if (condIdx >= 0 && condSet.size) {
const conditionOrder = [
  'Brand New',
  'Amazing as New',
  'Premium',
  'Excellent',
  'Good'
];

const condArray = Array.from(condSet)
  .filter(Boolean)
  .sort((a, b) => {
    const indexA = conditionOrder.findIndex(
      value => value.toLowerCase() === a.toLowerCase()
    );

    const indexB = conditionOrder.findIndex(
      value => value.toLowerCase() === b.toLowerCase()
    );

    // Known conditions follow the preferred order.
    // Any additional conditions appear afterward alphabetically.
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return a.localeCompare(b);
  });
      
      const frag = document.createDocumentFragment();
      condArray.forEach(val => {
        const o = document.createElement('option');
        o.value = val;
        o.textContent = val;
        frag.appendChild(o);
      });
      conditionEl.appendChild(frag);
      conditionEl.disabled = false;
    }
    
    if (autoSelectFirstAvailable) {
      const firstAvailable = product.variants.find(v => v.available);
      if (firstAvailable) {
        if (sizeIdx >= 0 && firstAvailable.options[sizeIdx]) sizeEl.value = firstAvailable.options[sizeIdx];
        if (condIdx >= 0 && firstAvailable.options[condIdx]) conditionEl.value = firstAvailable.options[condIdx];
      }
    }
    
    updateOptionAvailability(product);
    onDependentChange();
  }
  
  function findMatchingVariants(product, sizeVal, condVal) {
    const sizeIdx = optionIndexByRegex(product, sizeNameRegex);
    const condIdx = optionIndexByRegex(product, conditionNameRegex);
    
    return product.variants.filter(v => {
      let ok = true;
      if (sizeIdx >= 0 && sizeVal) ok = ok && ((v.options[sizeIdx] || '') === sizeVal);
      if (condIdx >= 0 && condVal) ok = ok && ((v.options[condIdx] || '') === condVal);
      return ok;
    });
  }
  
  function updateOptionAvailability(product) {
    if (!product) return;
    
    const sizeIdx = optionIndexByRegex(product, sizeNameRegex);
    const condIdx = optionIndexByRegex(product, conditionNameRegex);
    const currentSize = sizeEl.value || '';
    const currentCond = conditionEl.value || '';
    
    // Batch DOM updates
    requestAnimationFrame(() => {
      if (sizeIdx >= 0 && !sizeEl.disabled) {
        for (let i = 1; i < sizeEl.options.length; i++) {
          const opt = sizeEl.options[i];
          const val = opt.value;
          const matches = product.variants.filter(v => {
            const sameSize = ((v.options[sizeIdx] || '') === val);
            const condOk = condIdx >= 0 && currentCond ? ((v.options[condIdx] || '') === currentCond) : true;
            return sameSize && condOk;
          });
          const anyAvailable = matches.some(v => v.available);
          opt.disabled = !anyAvailable;
          opt.className = anyAvailable ? '' : 'cg-option-disabled';
        }
      }
      
      if (condIdx >= 0 && !conditionEl.disabled) {
        for (let i = 1; i < conditionEl.options.length; i++) {
          const opt = conditionEl.options[i];
          const val = opt.value;
          const matches = product.variants.filter(v => {
            const sameCond = ((v.options[condIdx] || '') === val);
            const sizeOk = sizeIdx >= 0 && currentSize ? ((v.options[sizeIdx] || '') === currentSize) : true;
            return sameCond && sizeOk;
          });
          const anyAvailable = matches.some(v => v.available);
          opt.disabled = !anyAvailable;
          opt.className = anyAvailable ? '' : 'cg-option-disabled';
        }
      }
    });
  }
  
  function renderAction(product, matchingVariants) {
    actionWrap.innerHTML = '';
    if (!product) return;
    
    if (!matchingVariants || matchingVariants.length === 0) {
      const btn = document.createElement('button');
      btn.textContent = 'No variant';
      btn.disabled = true;
      btn.className = 'cg-btn cg-btn-disabled';
      actionWrap.appendChild(btn);
      return;
    }
    
    const availableVariant = matchingVariants.find(v => v.available);
    if (availableVariant) {
      const a = document.createElement('a');
      a.href = `/products/${product.handle}?variant=${availableVariant.id}`;
      a.textContent = 'View Product';
      a.className = 'cg-btn cg-btn-primary';
      actionWrap.appendChild(a);
    } else {
      const btn = document.createElement('button');
      btn.textContent = 'Out of Stock';
      btn.disabled = true;
      btn.className = 'cg-btn cg-btn-disabled';
      actionWrap.appendChild(btn);
    }
  }
  
  // Debounce dependent changes
  let dependentTimeout;
  function onDependentChange() {
    clearTimeout(dependentTimeout);
    dependentTimeout = setTimeout(() => {
      const selModel = input.value?.trim() || modelSelect.value || '';
      if (!selModel) {
        actionWrap.innerHTML = '';
        return;
      }
      
      const product = findProductByTitle(selModel);
      if (!product) {
        actionWrap.innerHTML = '';
        return;
      }
      
      updateOptionAvailability(product);
      const sizeVal = sizeEl.value || '';
      const condVal = conditionEl.value || '';
      const matches = findMatchingVariants(product, sizeVal, condVal);
      renderAction(product, matches);
    }, 100);
  }
  
  function onModelChosen(title) {
    const product = findProductByTitle(title);
    if (!product || !productHasAvailableVariant(product)) return;
    
    modelSelect.value = title;
    populateDependentOptions(product, true);
  }
  
  // Event listeners with passive flag for better performance
  let inputTimeout;
  input.addEventListener('input', function() {
    clearTimeout(inputTimeout);
    inputTimeout = setTimeout(() => {
      renderList(this.value || '');
    }, DEBOUNCE_DELAY);
  });
  
  input.addEventListener('focus', function() {
    renderList(this.value || '');
  });
  
  let blurTimeout;
  input.addEventListener('blur', function() {
    // Only close if not clicking on list item
    blurTimeout = setTimeout(() => {
      closeList();
    }, 250); // Increased for iOS touch delay
  });
  
  // Prevent blur when touching/clicking inside dropdown
  listbox.addEventListener('mousedown', function(e) {
    e.preventDefault();
  });
  
  listbox.addEventListener('touchstart', function(e) {
    // Don't prevent default to allow scrolling
    e.stopPropagation();
  }, { passive: true });
  
  // Prevent clicks on listbox from closing dropdown
  listbox.addEventListener('click', function(e) {
    e.stopPropagation();
  });
  
  listbox.addEventListener('touchend', function(e) {
    e.stopPropagation();
  }, { passive: true });
  
  input.addEventListener('keydown', function(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (visibleItems.length) {
        const next = (activeIndex + 1) % visibleItems.length;
        setActiveIndex(next);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (visibleItems.length) {
        const prev = (activeIndex - 1 + visibleItems.length) % visibleItems.length;
        setActiveIndex(prev);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && visibleItems[activeIndex]) {
        const val = visibleItems[activeIndex].dataset.value;
        chooseModel(val);
        closeList();
      } else if (input.value?.trim()) {
        onModelChosen(input.value.trim());
        closeList();
      }
    } else if (e.key === 'Escape') {
      closeList();
      input.blur();
    }
  });
  
  // Optimized click outside handler
  document.addEventListener('click', function(ev) {
    if (!wrapper.contains(ev.target)) {
      closeList();
    }
  }, { passive: true, capture: true });
  
  // iOS-specific: handle touchend outside (but not when scrolling)
  let touchStartY = 0;
  let touchStartTime = 0;
  
  document.addEventListener('touchstart', function(ev) {
    touchStartY = ev.touches[0].clientY;
    touchStartTime = Date.now();
  }, { passive: true, capture: true });
  
  document.addEventListener('touchend', function(ev) {
    // Don't close if user was scrolling
    const touchEndTime = Date.now();
    const timeDiff = touchEndTime - touchStartTime;
    const wasTap = timeDiff < 300; // Quick tap vs scroll/drag
    
    if (wasTap && !wrapper.contains(ev.target) && listbox.style.display !== 'none') {
      closeList();
    }
  }, { passive: true, capture: true });
  
  modelSelect.addEventListener('change', function() {
    input.value = this.value || '';
    onModelChosen(this.value || '');
  });
  
  sizeEl.addEventListener('change', function() {
    const selModel = input.value || modelSelect.value || '';
    const product = findProductByTitle(selModel);
    if (product) updateOptionAvailability(product);
    onDependentChange();
  }, { passive: true });
  
  conditionEl.addEventListener('change', function() {
    const selModel = input.value || modelSelect.value || '';
    const product = findProductByTitle(selModel);
    if (product) updateOptionAvailability(product);
    onDependentChange();
  }, { passive: true });
  
  // Initial reset
  resetSelect(sizeEl, '— Available Size —');
  resetSelect(conditionEl, '— Available Condition —');
  actionWrap.innerHTML = '';
})();